/**
 * PTT Session Manager - Handles OAuth2 + PKCE and Guest authentication
 */

const OAUTH_BASE = 'https://dashboard.livefusion.app/oauth';
const API_BASE = 'https://dashboard.livefusion.app';
const CLIENT_ID = 'CxNabn-XMnzVWuSyycAY_g';
const REDIRECT_URI = window.location.origin + '/';
const SCOPES = 'openid profile email';

export class PTTSessionManager {
  constructor() {
    this._session = null;
    this._loadSession();
  }

  // --- Public API ---

  isAuthenticated() {
    return !!(this._session && (this._session.accessToken || this._session.guestToken));
  }

  isGuest() {
    return this._session?.isGuest === true;
  }

  getToken() {
    if (!this._session) return null;
    return this._session.isGuest ? this._session.guestToken : this._session.accessToken;
  }

  getAuthHeader() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  getSession() {
    return this._session;
  }

  // --- OAuth2 + PKCE Flow ---

  async startOAuthLogin() {
    const verifier = this._generateCodeVerifier();
    const challenge = await this._generateCodeChallenge(verifier);
    const state = this._generateState();

    // Store PKCE verifier and state for callback
    sessionStorage.setItem('ptt_pkce_verifier', verifier);
    sessionStorage.setItem('ptt_oauth_state', state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: SCOPES,
      state: state,
      code_challenge: challenge,
      code_challenge_method: 'S256'
    });

    window.location.href = `${OAUTH_BASE}/authorize?${params.toString()}`;
  }

  async handleOAuthCallback(code, state) {
    // Verify state
    const savedState = sessionStorage.getItem('ptt_oauth_state');
    if (!savedState) {
      throw new Error('Login session expired. Please log in again.');
    }
    if (state !== savedState) {
      throw new Error('Login session mismatch. Please log in again.');
    }

    const verifier = sessionStorage.getItem('ptt_pkce_verifier');
    if (!verifier) {
      throw new Error('Missing PKCE verifier');
    }

    // Exchange code for tokens
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: REDIRECT_URI,
      client_id: CLIENT_ID,
      code_verifier: verifier
    });

    const resp = await fetch(`${OAUTH_BASE}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Token exchange failed');
    }

    const tokens = await resp.json();

    // Clean up PKCE storage
    sessionStorage.removeItem('ptt_pkce_verifier');
    sessionStorage.removeItem('ptt_oauth_state');

    // Store OAuth session
    this._session = {
      isGuest: false,
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      expiresAt: Date.now() + (tokens.expires_in * 1000)
    };
    this._saveSession();

    return this._session;
  }

  // --- Guest Flow ---

  async joinAsGuest(qrToken, displayName) {
    const resp = await fetch(`${API_BASE}/ptt/guest/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qr_token: qrToken, display_name: displayName })
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}));
      throw new Error(err.detail || 'Failed to join as guest');
    }

    const data = await resp.json();

    this._session = {
      isGuest: true,
      guestToken: data.guest_token,
      sessionId: data.session_id,
      displayName: data.display_name,
      teamId: data.team.id,
      teamName: data.team.name,
      teamTag: data.team.tag,
      expiresAt: new Date(data.expires_at).getTime()
    };
    this._saveSession();

    return this._session;
  }

  // --- Session Validation ---

  _fetchWithTimeout(url, options, timeoutMs = 8000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    return fetch(url, { ...options, signal: controller.signal })
      .finally(() => clearTimeout(timer));
  }

  async validateSession() {
    const token = this.getToken();
    if (!token) return null;

    try {
      const resp = await this._fetchWithTimeout(`${API_BASE}/ptt/validate-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!resp.ok) {
        this.clearSession();
        return null;
      }

      const data = await resp.json();
      if (!data.valid) {
        // Try refresh once for OAuth sessions
        if (!this._session.isGuest && this._session.refreshToken) {
          const refreshed = await this._refreshToken();
          if (refreshed) {
            const retryResp = await this._fetchWithTimeout(`${API_BASE}/ptt/validate-session`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${this.getToken()}`,
                'Content-Type': 'application/json'
              }
            });
            if (retryResp.ok) {
              const retryData = await retryResp.json();
              if (retryData.valid) return retryData;
            }
          }
        }
        this.clearSession();
        return null;
      }

      return data;
    } catch (e) {
      console.warn('[SessionManager] Validation failed:', e);
      this.clearSession();
      return null;
    }
  }

  // --- Token Refresh ---

  async _refreshToken() {
    if (!this._session?.refreshToken) return false;

    try {
      const body = new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: this._session.refreshToken,
        client_id: CLIENT_ID
      });

      const resp = await this._fetchWithTimeout(`${OAUTH_BASE}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      });

      if (!resp.ok) return false;

      const tokens = await resp.json();
      this._session.accessToken = tokens.access_token;
      this._session.refreshToken = tokens.refresh_token;
      this._session.expiresAt = Date.now() + (tokens.expires_in * 1000);
      this._saveSession();
      return true;
    } catch {
      return false;
    }
  }

  // --- Session Lifecycle ---

  clearSession() {
    this._session = null;
    localStorage.removeItem('ptt_session');
    sessionStorage.removeItem('ptt_session'); // migrate from old location
    sessionStorage.removeItem('ptt_pkce_verifier');
    sessionStorage.removeItem('ptt_oauth_state');
  }

  // --- PKCE Helpers ---

  _generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this._base64URLEncode(array);
  }

  async _generateCodeChallenge(verifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this._base64URLEncode(new Uint8Array(digest));
  }

  _generateState() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return this._base64URLEncode(array);
  }

  _base64URLEncode(buffer) {
    return btoa(String.fromCharCode(...buffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  // --- Storage ---

  _saveSession() {
    if (this._session) {
      localStorage.setItem('ptt_session', JSON.stringify(this._session));
    }
  }

  _loadSession() {
    try {
      // Load from localStorage (persists across browser restarts)
      let raw = localStorage.getItem('ptt_session');
      // Migrate from old sessionStorage location
      if (!raw) {
        raw = sessionStorage.getItem('ptt_session');
        if (raw) {
          localStorage.setItem('ptt_session', raw);
          sessionStorage.removeItem('ptt_session');
        }
      }
      if (raw) {
        this._session = JSON.parse(raw);
        // Check expiry
        if (this._session.expiresAt && Date.now() > this._session.expiresAt) {
          // Expired — for guests, clear. For OAuth, try refresh later.
          if (this._session.isGuest) {
            this.clearSession();
          }
        }
      }
    } catch {
      this._session = null;
    }
  }
}
